/**
 * A grass tuft for the stylized look: five tapered blades leaning out from one root, dark at the
 * root and light at the tip, about a quarter of a unit tall and 15 triangles. Its normals point up,
 * so the blades take the light like the ground they grow from.
 */
import * as THREE from 'three';

import { hash2 } from '@core/math';

const BLADES = 5;
const WIDTH = 0.028;

export function tuftGeometry(): THREE.BufferGeometry {
  const root = new THREE.Color(0x244a1b);
  const tip = new THREE.Color(0x649a44);
  const mid = new THREE.Color().lerpColors(root, tip, 0.55);
  const position: number[] = [];
  const color: number[] = [];
  const normal: number[] = [];
  const index: number[] = [];
  const vertex = (x: number, y: number, z: number, c: THREE.Color): void => {
    position.push(x, y, z);
    color.push(c.r, c.g, c.b);
    normal.push(0, 1, 0);
  };
  for (let b = 0; b < BLADES; b++) {
    const angle = (b / BLADES) * Math.PI * 2 + hash2(b, 3) * 0.8;
    const lean = 0.06 + 0.07 * hash2(b, 5);
    const height = 0.17 + 0.11 * hash2(b, 7);
    const ox = Math.cos(angle);
    const oz = Math.sin(angle);
    // across the blade, perpendicular to where it leans
    const sx = -oz * WIDTH;
    const sz = ox * WIDTH;
    const first = position.length / 3;
    vertex(sx, 0, sz, root);
    vertex(-sx, 0, -sz, root);
    const ml = lean * 0.4;
    vertex(ox * ml + sx * 0.7, height * 0.55, oz * ml + sz * 0.7, mid);
    vertex(ox * ml - sx * 0.7, height * 0.55, oz * ml - sz * 0.7, mid);
    vertex(ox * lean, height, oz * lean, tip);
    index.push(
      first,
      first + 1,
      first + 2,
      first + 1,
      first + 3,
      first + 2,
      first + 2,
      first + 3,
      first + 4,
    );
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(position, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normal, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(color, 3));
  geometry.setIndex(index);
  return geometry;
}
